import Select from "lib/common/controls/Select";
import DateRange, {
    dateToString,
    isDateRangeValid,
} from "lib/hex/views/components/DateRange";
import OptionGroup from "lib/hex/views/components/OptionGroup";
import { registerForm } from "lib/hex/views/formRegistry";
import PropTypes from "prop-types";
import React from "react";
import useKeyValueState from "lib/hex/views/components/useKeyValueState";
import {
    fromCommaSafeArray,
    stringifyJson,
    toCommaSafeArray,
    unstringifyJson,
} from "lib/hex/views/components/serializeParams";

const FormView = ({onChange, state, data}) => (
    <div>
        <div className="form-section form-horizontal">
            <div className="form-section-inner">
                <h3>Center</h3>
                <div className="form-group">
                    <div className="col-sm-12">
                        <Select
                            name="centerno"
                            className="center form-control"
                            onCommitChange={(v) => onChange("centerno", v)}
                            value={state.centerno}
                            options={data.centers}
                            required
                        />
                    </div>
                </div>
                <DateRange
                    start={state.start}
                    end={state.end}
                    min={data.daterange.from}
                    max={data.daterange.to}
                    onChange={onChange}
                    disabled={!state.centerno}
                    required
                />
            </div>
        </div>
    </div>
);

const selectShape = PropTypes.arrayOf(
    PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
    })
);

FormView.propTypes = {
    onChange: PropTypes.func.isRequired,
    state: PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string,
        centerno: PropTypes.string,
    }),
    data: PropTypes.shape({
        centerno: selectShape,
        daterange: PropTypes.shape({
            from: PropTypes.array,
            to: PropTypes.array
        }),
    }),
};

const initialStateWithDefaults = (state = {}) => {
    const newState = {
        start: dateToString(state.start),
        end: dateToString(state.end),
        centerno: state.centerno || "",
    };
    return unstringifyJson(
        fromCommaSafeArray(newState)
    );
};

const serverDataParams = params =>
    stringifyJson(
        toCommaSafeArray(params)
    );

const initialData = {
    centers: [],
    daterange: {from: [], to: []},
};

const ECMOExportForm = ({initialState, needServerData, onReady, onNotReady}) => {
    const [ state, setState ] = React.useState(() =>
        initialStateWithDefaults(initialState));
    React.useEffect(
        () => setState(initialStateWithDefaults(initialState)),
        [ initialState ],
    );
    const [ data, setDataKey ] = useKeyValueState(initialData);

    React.useEffect(() => {
        needServerData({
            endpoint: "/centers",
            loadingMessage: "Loading Centers...",
            onData: (data) => {
                data = data.map(it => ({
                    label: `${it.centerNo}: ${it.centerName}`,
                    value: it.centerNo,
                }));
                data.unshift({ label: "", value: "" });
                setDataKey("centers", data);
            },
        });
    }, []);


    React.useEffect(() => {
        const { centerno } = state;
        if (!centerno) {
            return; // not yet!
        }
        needServerData({
            endpoint: "/date-range",
            loadingMessage: "Loading Available Date Range...",
            params: serverDataParams({
                centerno,
            }),
            onData: (data) => setDataKey("daterange", {
                from: data.from,
                to: data.to,
            }),
        });
    }, [ state.centerno ]);

    React.useEffect(() => {
        // update the submit button and data
        if (isDateRangeValid(state.start, state.end) && !!state.centerno) {
            onReady(serverDataParams({
                ...state,
            }));
        } else {
            onNotReady();
        }
    }, [ state, data ]);

    const handleChange = React.useCallback(
        (key, value) =>
            setState(s => ({ ...s, [key]: value })),
        [ setState ],
    );

    return (
        <FormView
            state={state}
            data={data}
            onChange={handleChange}
        />
    );
};

ECMOExportForm.propTypes = {
    initialState: PropTypes.shape({
        start: PropTypes.any,
        end: PropTypes.any,
        centerno: PropTypes.string,
    }),
    needServerData: PropTypes.func.isRequired,
    onReady: PropTypes.func.isRequired,
    onNotReady: PropTypes.func.isRequired,
};

ECMOExportForm.canRerun = params => !!(params && params.centerno);

ECMOExportForm.apiPrefix = "ecmo";

registerForm("ecmo-export", ECMOExportForm);


// WEBPACK FOOTER //
// ./export_forms/api-ecmo-2026.09.21-jar/hybrid-jsclient/ECMOExportForm.js